import Model from '../../framework/model.js';
import Utils from '../../framework/utils.js';
import JobPost from './job-post-model.js';
import JobLocationModel from './job-location-model.js';
import MapLocationModel from './map-location-model.js';
import FilterModel from './filter-model.js';
import PaginationModel from './pagination-model.js';
import Globals from '../../framework/globals.js';

const defaults = {
  dataUrl: 'https://api.smartrecruiters.com/v1/companies/fortunebrands/postings'
}

// FBHSCareersModel
// ================
// 
// Manages the Career Map app state

class FBHSCareersModel extends Model {
  constructor(data = {}) {
    super({ data: data, defaults: defaults});
    Utils.bindMethods(this, ['loadPosts', 'parsePosts', 'onFilterChange', 'parseFilters']);

    this._allPosts = {};
    this.set('filterModel', new FilterModel());
    this.set('paginationModel', new PaginationModel({
      itemsPerPage: 9
    }))
  }

  init() {
    this.loadPosts()
      .then(this.parsePosts)
      .then(this.parseFilters)
      .then(() => {
        let params = new URLSearchParams(window.location.search);
        let initFilter = {
          location: params.get('location') ? params.get('location').split(',') : [],
          employeeType: params.get('employeeType') ? params.get('employeeType').split(',') : [],
          department: params.get('department') ? params.get('department').split(',') : [],
          brand: params.get('brand') ? params.get('brand').split(',') : [],
          keyword: params.get('keyword')
        }
        if (Object.values(initFilter).filter((a) => a).length) {
          this.get('filterModel').setFilter(initFilter, this.get('posts'));
        }
        this.onFilterChange(null);
        this.get('filterModel').addEventListener('Model.Change', this.onFilterChange);
        this.dispatchEvent(new Event(FBHSCareersModel.Events.READY));
      });
  }

  // `loadPosts(offset)`
  //
  // Fetches the job post data from SmartRecruiters. Since SR limits queries to 100 results,
  // we need to load these piecemeal and aggregate the results.
  //
  // Returns a promise of more results if we have not finished loading everything, otherwise
  // returns the aggregate of job posts.
  loadPosts(offset = 0) {
    return fetch(`${this.get('dataUrl')}?offset=${offset}`)
      .then((response) => response.json())
      .then((data) => {
        data.content.forEach((item) => {
          this._allPosts[item.id] = item;
        })
        if (data.offset + data.limit < data.totalFound) {
          //load all the rest
          let fetches = [];
          let fetchOffset = data.offset;
          let fetchLimit = data.limit;
          let fetchTotal = data.totalFound;
          while (fetchOffset + fetchLimit < fetchTotal) {
            fetchOffset += fetchLimit;
            fetches.push(fetch(`${this.get('dataUrl')}?offset=${fetchOffset}`).then((response) => response.json()))
          }
          return Promise.all(fetches).then((datasets) => {
            datasets.forEach((set) => {
              set.content.forEach((item) => {
                this._allPosts[item.id] = item;
              });
            })

            return this._allPosts;
          })
        } else {
          return this._allPosts;
        }
      }).catch((error) => {
        console.log(error);
        return this._allPosts;
      });
  }

  // `parsePosts(postsData)`
  //
  // Makes a best effort to map a location name with the job post
  // and wraps the end result in a JobPostModel
  //
  // Returns arrays of JobPostModels and JobLocationModels
  parsePosts(postsData) {
    let posts = [];
    Object.values(postsData).sort((a, b) => Date.parse(b.releasedDate) - Date.parse(a.releasedDate)).forEach((post) => {
      let jobPost = new JobPost(post);
      jobPost.set('jobLocation', JobLocationModel.registerJobLocation(post.location));
      posts.push(jobPost);
    })
    posts = posts.filter((a) => {
      let brandExceptions = [
        'Moen (Internal Opportunity Program)'
      ];
      return !brandExceptions.includes(a.getBrandLabel().trim());
    })
    this.set('posts', posts);

    this.set('locations', JobLocationModel._all);

    let mapLocations = window.careers_map.locations
      .map(ml => new MapLocationModel(ml))
      .filter(ml => ml.toJobLocationModel() != null);
    this.set('mapLocations', mapLocations);

    delete this._allPosts;
    
    return Promise.resolve({ posts: posts, locations: JobLocationModel._all });
  }

  // `parseFilters(params)`
  //
  // Given the results of `parsePosts`, passes the job posts and job locations to the FilterModel
  // to add and sort the filter values.
  parseFilters(params) {
    params.posts.forEach((post) => {
      this.get('filterModel').parsePost(post);
    })
    params.locations.forEach((loc) => {
      this.get('filterModel').parseLocation(loc);
    })
    this.get('filterModel').sortFilters();
    return true;
  }

  // `onFilterChange()`
  //
  // Update pagination model whenever the filter changes
  onFilterChange(evt) {
    if (!evt || evt.data.path == 'currentFilter') {
      let total_count = 0;
      this.get('posts').forEach((post) => {
        post.set('filterMatch', this.get('filterModel').checkPost(post));
        if (post.get('filterMatch')) total_count++;
      })
      this.set('paginationModel.totalItems', total_count)

      // update url
      let queryString = new URLSearchParams();
      let currFilter = this.get('filterModel.currentFilter');
      for (let key in currFilter) {
        if (currFilter[key] && (Array.isArray(currFilter[key]) ? currFilter[key].length : true)) {
          if (key == 'location') {
            queryString.append(key, currFilter[key].map((loc) => loc.get('id')).join(','));
          } else {
            queryString.append(key, Array.isArray(currFilter[key]) ? currFilter[key].join(',') : currFilter[key]);
          }
        }
      }
      window.history.replaceState(window.history.state, null, `${window.location.pathname}?${queryString.toString()}`);
    }
  }
}

FBHSCareersModel.Events = {
  READY: 'CareersModel.Ready'
};

export default FBHSCareersModel;